import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { Button, ButtonType, ButtonVariant, MainTab, Notify } from 'components/core';
import { IFormValues } from 'helpers/visualizationEditor/types';
import { buildVisualizationTabItems } from 'molecules/visualizationEditor/visualizationTabItems';
import { ChartPreview } from 'molecules/visualizationEditor/ChartPreview';
import { useQueryValidity } from 'molecules/visualizationEditor/hooks/useQueryValidity';
import styles from './ConfigLayoutVisualizations.module.css';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  currentVisualizationState,
  visualizationsState,
  Visualization,
  newItemState,
  builderIssuesState,
  builderEffectiveOutputState,
  visualisationRolesState,
  visualisationDashboardsState,
} from 'states';
import moment from 'moment';
import { useDashboardService, useVisualizationService } from 'services';
import {
  TAB_FIELD_MAP,
  TAB_NAMES,
  TAB_DISPLAY_ORDER,
  TEMPLATE_TAB_LABEL,
  TEMPLATE_KEYS,
} from 'constants/appConstants';
import { allDashboards } from 'states/atoms';
import { BuilderPreviewRow } from 'molecules/kpiBuilder/BuilderPreviewRow';
import { LivePreview } from 'molecules/kpiBuilder/LivePreview';
import { BuilderModeBar } from 'molecules/kpiBuilder/BuilderModeBar';
import { useBuilderSync } from 'molecules/kpiBuilder/hooks/useBuilderSync';
import { useAdvancedAccess } from 'molecules/kpiBuilder/hooks/useAdvancedAccess';
import { FormBridge } from 'helpers/kpiBuilder/formBridge';
import { builderSaveExtras, tabHasBuilderIssue } from 'helpers/kpiBuilder/builderTabs';
import { buildDetailVisualization } from 'helpers/kpiBuilder/detailVisualization';
import { ValidationErrorBar } from 'components/core/bars/ValidationErrorBar';
import { TemplateWarningBar } from 'components/core/bars/TemplateWarningBar';
import {
  isValidJsonOrEmpty,
  isStarterTemplate,
  splitConfig,
  mergeConfig,
  buildErrorSummary,
  translateTabName,
} from 'helpers';

export const ConfigLayoutVisualizations = () => {
  const { t } = useTranslation();
  const [tabItems, setTabItems] = useState<any>([]);
  const [invalidQuery, setInvalidQuery] = useState(false);
  const [saveAttempted, setSaveAttempted] = useState(false);
  const [templateWarnTabs, setTemplateWarnTabs] = useState<string[]>([]);
  const [showTemplateConfirm, setShowTemplateConfirm] = useState(false);
  const [confirmedValues, setConfirmedValues] = useState<IFormValues | null>(null);
  const [currentVisualization, setCurrentVisualization] = useRecoilState(currentVisualizationState);
  const [visualizations, setVisualizations] = useRecoilState(visualizationsState);
  const setIsNewItem = useSetRecoilState(newItemState);
  const [visualizationRoles] = useRecoilState(visualisationRolesState);
  const [visualizationDashboards] = useRecoilState(visualisationDashboardsState);
  const visualizationService = useVisualizationService();
  const dashboardService = useDashboardService();
  const [, setDashboards] = useRecoilState(allDashboards);
  const builderIssues = useRecoilValue(builderIssuesState);
  const builderOutput = useRecoilValue(builderEffectiveOutputState);
  const canUseJson = useAdvancedAccess();

  const isExisting = !!currentVisualization.id;

  useEffect(() => { getVisualizations(); }, []);
  useEffect(() => { getDashboards(); }, []);

  const getVisualizations = () => {
    visualizationService.getAllVisualizations().then(
      (r: any) => { if (r.length > 0) setVisualizations(r); },
      (err: any) => Notify.error(err)
    );
  };

  const getDashboards = () => {
    dashboardService.getDashboards().then(
      (r: any) => { if (r.length > 0) setDashboards(r); },
      (err: any) => Notify.error(err)
    );
  };

  const validationSchema = Yup.object().shape({
    title:             Yup.string().required(t('VALIDATION.TITLE_REQUIRED')),
    subTitle:          Yup.string(),
    chartType:         Yup.string().required(t('VALIDATION.CHART_TYPE_REQUIRED')),
    queries:           Yup.string().required(t('VALIDATION.QUERY_REQUIRED')),
    config:            Yup.string().required(t('VALIDATION.CONFIG_REQUIRED')),
    kpiFilters:        isValidJsonOrEmpty('KPI filters', t),
    visualizationCode: Yup.string()
      .required(t('VALIDATION.VIZ_CODE_REQUIRED'))
      .test('unique-visualization-code', t('VALIDATION.VIZ_CODE_DUPLICATE'), (value) => {
        if (!value) return true;
        const trimmed = value.trim().toLowerCase();
        const duplicate = visualizations.some((v: Visualization) => {
          if (isExisting && v.id === currentVisualization.id) return false;
          return (v.visualizationCode || '').trim().toLowerCase() === trimmed;
        });
        return !duplicate;
      }),
  });

  const visualizationData = currentVisualization.data[0];
  const initialSplit = splitConfig(visualizationData.additionalConfig.config || '');

  const {
    register,
    handleSubmit,
    formState,
    reset,
    setValue,
    getValues,
    watch,
    setError,
    clearErrors,
  } = useForm<IFormValues>({
    resolver: yupResolver(validationSchema),
    mode: 'all',
    defaultValues: {
      title:             currentVisualization.title || '',
      chartType:         visualizationData.info.chartType || '',
      subTitle:          visualizationData.info.subTitle || '',
      visualizationCode: currentVisualization.visualizationCode || '',
      queries:           visualizationData.data.query || '',
      config:            initialSplit.restConfigStr,
      kpiFilters:        initialSplit.kpiFiltersStr,
      accessConfig:      JSON.stringify(visualizationRoles) || [],
    },
  });

  const { errors, isSubmitting, isValid } = formState;

  const watchedChartType = watch('chartType');
  const watchedTitle = watch('title');
  const [watchedQueries, watchedConfig, watchedCode, watchedSubTitle] = watch(['queries', 'config', 'visualizationCode', 'subTitle']);

  // KPI Builder: generates the query/config into the form fields above.
  const builderForm: FormBridge = {
    get: (field) => getValues(field) ?? '',
    set: (field, value, validate = false) => setValue(field, value, { shouldValidate: validate }),
  };
  const { builderMode, switchToJson, switchToBuilder, canSwitchToBuilder, keepGeneratedFields } = useBuilderSync(builderForm, watchedTitle);

  // ── Tabs with hard RHF errors ───────────────────────────────────────────────
  const tabsWithErrors = Object.entries(TAB_FIELD_MAP).reduce<number[]>((acc, [tabIdx, fields]) => {
    const hasError = fields.some((f) => !!errors[f as keyof IFormValues]) || (Number(tabIdx) === 1 && invalidQuery);
    if (hasError) acc.push(Number(tabIdx));
    return acc;
  }, []);

  const allTabsWithErrors: number[] = (() => {
    if (!saveAttempted) return tabsWithErrors;
    const withRequiredTabs = [...tabsWithErrors];
    if (visualizationRoles.length === 0) withRequiredTabs.push(4);
    if (visualizationDashboards.length === 0) withRequiredTabs.push(5);
    return Array.from(new Set(withRequiredTabs));
  })();

  // Readable tab names for the error bar
  const errorTabNames = TAB_DISPLAY_ORDER.filter((i) => allTabsWithErrors.includes(i)).map((i) => TAB_NAMES[i]);

  // ── Tab label with dot indicator ────────────────────────────────────────────
  // `name` stays the canonical English key for comparisons (templateWarnTabs
  // etc.) — only the rendered text is translated.
  const tabLabel = (name: string, idx: number) => {
    const hasHardError    = allTabsWithErrors.includes(idx);
    const hasTemplateWarn = !isExisting && templateWarnTabs.includes(name);
    const hasBuilderWarn = tabHasBuilderIssue(builderIssues, idx);
    const displayName = translateTabName(name, t);
    if (!hasHardError && !hasTemplateWarn && !hasBuilderWarn) return displayName;
    return (
      <span className={styles.tabLabelWrap}>
        {displayName}
        <span
          title={
            hasHardError
              ? t('VALIDATION.TAB_HAS_ERRORS')
              : hasBuilderWarn
                ? t('VALIDATION.TAB_HAS_WARNING')
                : t('VALIDATION.TAB_TEMPLATE_NOT_EDITED')
          }
          className={`${styles.tabDot} ${hasHardError ? styles.tabDotError : styles.tabDotWarning}`}
        />
      </span>
    );
  };

  useEffect(() => {
    setTabItems(buildVisualizationTabItems({
      form: { register, setValue, formState, watch },
      tabLabel, builderMode, keepGeneratedFields, isExisting, invalidQuery, saveAttempted, chartType: watchedChartType,
    }));
  }, [
    errors.title,
    errors.chartType,
    errors.visualizationCode,
    errors.queries,
    errors.config,
    errors.kpiFilters,
    invalidQuery,
    currentVisualization,
    watchedChartType,
    saveAttempted,
    visualizationRoles.length,
    visualizationDashboards.length,
    templateWarnTabs,
    builderMode,
    keepGeneratedFields,
    builderIssues,
    t,
  ]);

  useEffect(() => {
    const d = currentVisualization.data[0];
    reset();
    setInvalidQuery(false);
    const split = splitConfig(d.additionalConfig.config || '');
    setValue('title',             currentVisualization.title || '');
    setValue('subTitle',          d.info.subTitle || '');
    setValue('chartType',         d.info.chartType || '');
    setValue('queries',           d.data.query || '');
    setValue('config',            split.restConfigStr);
    setValue('kpiFilters',        split.kpiFiltersStr);
    setValue('visualizationCode', currentVisualization.visualizationCode || '');
    setValue('accessConfig',      JSON.stringify(visualisationRolesState) || []);
    setSaveAttempted(false);
    setShowTemplateConfirm(false);
    setConfirmedValues(null);
    setTemplateWarnTabs([]);
  }, [currentVisualization]);

  useQueryValidity({
    rawType: watchedChartType,
    queries: watchedQueries,
    invalidQuery,
    onValid: () => { setInvalidQuery(false); clearErrors('queries'); },
    onInvalid: () => { setInvalidQuery(true); setError('queries', { type: 'pattern', message: t('VALIDATION.INVALID_JSON') }); },
  });

  // Saves the click-through list of a gap KPI. Returns false (after telling the user) when it could not be saved.
  const saveDetailList = async (list: Parameters<typeof buildDetailVisualization>[0], existingId?: number): Promise<boolean> => {
    try {
      const response = await visualizationService.createOrUpdateVisualization(buildDetailVisualization(list, visualizationRoles, existingId));
      if (response?.[0] && !response.statusInfo?.errorMessage) return true;
      Notify.error(response?.statusInfo?.errorMessage || 'The visualization was saved, but its list of learners could not be saved. Save it again to retry.');
    } catch {
      Notify.error('The visualization was saved, but its list of learners could not be saved. Save it again to retry.');
    }
    return false;
  };

  const onSubmit = (values: IFormValues) => {
    if (invalidQuery) return;
    const { title, chartType, subTitle, queries, visualizationCode, config, kpiFilters } = values;
    const temp = visualizationDashboards.map((i: any) => i.dashboardId);

    const mergedConfig = mergeConfig(config, kpiFilters);
    // In builder mode the recipe decides the backend handler; in JSON mode the saved one is kept.
    const { handlerKey, list } = builderSaveExtras(builderMode, builderOutput, visualizationData.info.handlerKey);

    const detail: Visualization = {
      title,
      isPinned: false,
      isDecimal: false,
      type: 'visualization',
      visualizationCode,
      visualizationPermission: temp,
      data: [{
        info: { title, subTitle, chartType, ...(handlerKey ? { handlerKey } : {}) },
        data: { query: queries },
        access: {
          escapedAccessConfig: visualizationRoles,
          accessConfig: JSON.stringify(visualizationRoles),
        },
        dashboards: JSON.stringify(temp),
        additionalConfig: { config: mergedConfig },
      }],
    };
    if (currentVisualization.id) detail.id = currentVisualization.id;

    // A gap KPI also needs its click-through list. Never overwrite a list that belongs to another KPI.
    const existingList = list ? visualizations.find((v: Visualization) => v.visualizationCode === list.visualizationCode) : undefined;
    if (list && existingList && !currentVisualization.id) {
      Notify.error(`A visualization with the code “${list.visualizationCode}” already exists. Change the title so this one gets its own list.`);
      return;
    }

    visualizationService.createOrUpdateVisualization(detail).then(
      async (response) => {
        if (!response?.[0]) {
          Notify.error(response?.statusInfo?.errorMessage || t('VALIDATION.ERROR_GENERIC'));
          return;
        }
        // A failed save comes back as HTTP 200 with statusInfo, so check it as the main save does. The success toast waits for both saves.
        if (list && !(await saveDetailList(list, existingList?.id))) return;
        const isUpdate = !!currentVisualization.id;
        Notify.success(isUpdate ? t('VISUALIZATION.SAVE_SUCCESS') : t('VISUALIZATION.CREATE_SUCCESS'));
        setIsNewItem(false);
        getVisualizations();
        setCurrentVisualization({} as Visualization);
        reset();
        setSaveAttempted(false);
        setTemplateWarnTabs([]);
        setShowTemplateConfirm(false);
        setConfirmedValues(null);
      },
      (err) => Notify.error(err?.message || err || t('VALIDATION.ERROR_GENERIC'))
    );
  };

  const handleBack = useCallback(() => {
    const unsavedIdx = visualizations.findIndex((v: Visualization) => v.id === undefined);
    if (unsavedIdx !== -1) {
      const updated = [...visualizations];
      updated.splice(unsavedIdx, 1);
      setVisualizations(updated);
      setIsNewItem(false);
    }
    setCurrentVisualization({} as Visualization);
    reset();
  }, [visualizations, setVisualizations, setIsNewItem, setCurrentVisualization, reset]);

  const VISUALIZATION_ERROR_FIELD_CHECKS = [
    { field: 'title' as const,             tabLabel: 'Info' },
    { field: 'chartType' as const,         tabLabel: 'Info' },
    { field: 'visualizationCode' as const, tabLabel: 'Info' },
    { field: 'queries' as const,           tabLabel: 'Data', extraInvalid: invalidQuery, extraMessage: t('VALIDATION.INVALID_JSON') },
    { field: 'kpiFilters' as const,        tabLabel: 'Filters' },
    { field: 'config' as const,            tabLabel: 'Additional Config' },
  ];

  const notifyValidationErrors = (currentErrors: typeof errors) => {
    const hasAccess = visualizationRoles.length > 0;
    const hasDashboards = visualizationDashboards.length > 0;
    Notify.error(buildErrorSummary(currentErrors, VISUALIZATION_ERROR_FIELD_CHECKS, [
      { label: 'Access', message: t('VALIDATION.ACCESS_ROLE_REQUIRED_VISUALIZATION'), passed: hasAccess },
      { label: 'Dashboards', message: t('VALIDATION.VISUALIZATION_DASHBOARDS_REQUIRED'), passed: hasDashboards },
    ], t));
  };

  const handleSaveClick = useCallback(() => {
    setSaveAttempted(true);
    handleSubmit(
      (currentValues) => {
        const hasAccess = visualizationRoles.length > 0;
        const hasDashboards = visualizationDashboards.length > 0;
        if (invalidQuery || !hasAccess || !hasDashboards) {
          notifyValidationErrors({});
          return;
        }

        // The builder generates real values, so there is no starter template to warn about.
        const warnings = builderMode ? [] : TEMPLATE_KEYS
          .filter((key) => isStarterTemplate(key, currentValues[key as keyof IFormValues] as string, watchedChartType))
          .map((key) => TEMPLATE_TAB_LABEL[key]);

        setTemplateWarnTabs(warnings);

        if (warnings.length > 0 && !isExisting) {
          setConfirmedValues(currentValues);
          setShowTemplateConfirm(true);
          return;
        }

        onSubmit(currentValues);
      },
      // RHF passes the freshly-computed errors here, so this can't go stale
      // the way reading `errors` from the render closure could.
      (freshErrors) => notifyValidationErrors(freshErrors)
    )();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    setSaveAttempted,
    handleSubmit,
    visualizationRoles,
    visualizationDashboards,
    invalidQuery,
    watchedChartType,
    isExisting,
    builderMode,
    onSubmit,
  ]);

  const handleConfirmSave = useCallback(() => {
    setShowTemplateConfirm(false);
    if (confirmedValues) onSubmit(confirmedValues);
  }, [confirmedValues, onSubmit]);

  const handleCancelConfirm = useCallback(() => {
    setShowTemplateConfirm(false);
    setConfirmedValues(null);
  }, []);

  const isSaveDisabled = isSubmitting ||
    (saveAttempted && (!isValid || invalidQuery || visualizationRoles.length === 0 || visualizationDashboards.length === 0));

  return (
    <div className={styles.detailPage}>
      <div className={styles.detailHeader}>
        <div className={styles.headerTop}>
          <div className={styles.headerLeft}>
            <button className={styles.backBtn} onClick={handleBack} title="Back to visualizations">
              <span className="material-icons">arrow_back</span>
            </button>
            <div>
              <div className={styles.vizTitle}>{currentVisualization.title}</div>
              <div className={styles.vizMeta}>
                {visualizationData.info.chartType && (
                  <span className={styles.chartTypeBadge}>{visualizationData.info.chartType}</span>
                )}
                {currentVisualization.timestamp && currentVisualization.timestamp > 0
                  ? moment.unix(currentVisualization.timestamp / 1000).format("DD MMM YYYY")
                  : null}
              </div>
            </div>
          </div>
          <div className={styles.headerActions}>
            <Button
              className={styles.cancelBtn}
              type={ButtonType.secondary}
              variant={ButtonVariant.outline}
              onClick={handleBack}
            >
              <span className="material-icons me-1" style={{ verticalAlign: 'sub' }}>close</span>
              {t('ACTION.CANCEL')}
            </Button>
            <div className={styles.saveWrapper}>
              <Button
                className={styles.saveBtn}
                type={ButtonType.secondary}
                disabled={isSaveDisabled}
                onClick={handleSaveClick}
              >
                {isSubmitting && <span className="spinner-border spinner-border-sm me-2" />}
                <span className="material-icons me-1" style={{ verticalAlign: 'sub' }}>done</span>
                {t('ACTION.SAVE')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {saveAttempted &&
        (!isValid || invalidQuery || visualizationRoles.length === 0 || visualizationDashboards.length === 0) &&
        !showTemplateConfirm && <ValidationErrorBar tabsWithErrors={errorTabNames} />}

      {showTemplateConfirm && (
        <TemplateWarningBar
          warnTabs={templateWarnTabs}
          onConfirm={handleConfirmSave}
          onCancel={handleCancelConfirm}
        />
      )}

      <div className={styles.detailBody}>
        <form onSubmit={(e) => { e.preventDefault(); handleSaveClick(); }}>
          <BuilderPreviewRow builderMode={builderMode}>
            <div className={styles.chartPreview}>
              <div className={styles.chartPreviewInner}>
                <LivePreview
                  source={{
                    code: watchedCode,
                    queries: watchedQueries,
                    config: watchedConfig,
                    handlerKey: builderSaveExtras(builderMode, builderOutput, visualizationData.info.handlerKey).handlerKey,
                  }}
                  chartType={watchedChartType}
                  title={watchedTitle}
                  subTitle={watchedSubTitle}
                  fallback={<ChartPreview rawType={watchedChartType} title={currentVisualization.title} queries={watchedQueries} />}
                />
              </div>
            </div>
          </BuilderPreviewRow>
          <BuilderModeBar
            builderMode={builderMode}
            canUseJson={canUseJson}
            onSwitchToJson={switchToJson}
            onSwitchToBuilder={canSwitchToBuilder ? switchToBuilder : undefined}
          />
          <MainTab tabItems={tabItems} />
        </form>
      </div>
    </div>
  );
};
